import { TextField } from "@buildblockinc/buildblock-design-system";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

interface DecimalNumberFieldProps {
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  suffix?: ReactNode;
}

export function DecimalNumberField({ value, onChange, placeholder, suffix }: DecimalNumberFieldProps) {
  const [display, setDisplay] = useState(value ? String(value) : "");

  useEffect(() => {
    setDisplay((prev) => {
      const prevNum = prev === "" || prev === "." ? 0 : Number(prev);
      if (prevNum === value) return prev;
      return value ? String(value) : "";
    });
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = e.target.value.replace(/[^0-9.]/g, "");
    if (sanitized.split(".").length > 2) return;
    setDisplay(sanitized);
    if (sanitized === "" || sanitized === ".") {
      onChange(0);
      return;
    }
    const num = Number(sanitized);
    if (!Number.isNaN(num)) onChange(num);
  };

  return (
    <TextField.Input
      inputMode="decimal"
      placeholder={placeholder}
      suffix={suffix}
      value={display}
      onChange={handleChange}
    />
  );
}

interface IntegerNumberFieldProps {
  value: number;
  onChange: (value: number) => void;
  max?: number;
  placeholder?: string;
  suffix?: ReactNode;
}

export function IntegerNumberField({ value, onChange, max, placeholder, suffix }: IntegerNumberFieldProps) {
  const [display, setDisplay] = useState(value ? String(value) : "");

  useEffect(() => {
    setDisplay((prev) => {
      const prevNum = prev === "" ? 0 : Number(prev);
      if (prevNum === value) return prev;
      return value ? String(value) : "";
    });
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    if (raw === "") {
      setDisplay("");
      onChange(0);
      return;
    }
    const num = Number(raw);
    if (max !== undefined && num > max) return;
    setDisplay(raw);
    onChange(num);
  };

  return (
    <TextField.Input
      inputMode="numeric"
      placeholder={placeholder}
      suffix={suffix}
      value={display}
      onChange={handleChange}
    />
  );
}
